import React from 'react';
import { MediaItem } from '../../api/structs/media';
import { RegenerateWatchMap, regenerateStatusOf } from '../clips/sections/regenerateWatch';
import { readRegenerateFailure, regenerateFailureText } from '../clips/regenerateFailure';

interface SceneMediaStatusProps {
  /** The LIVE item this line is about; nothing renders without one. */
  item: MediaItem | undefined;
  /**
   * Picks the copy and the test ids only — the shape is deliberately the same
   * red line in both panes of the scene dialog. It was previously reachable
   * ONLY from the Frames pane, which is why a rejected VIDEO regeneration (the
   * live incident: a Veo RAI content block) had nowhere at all to render.
   */
  role: 'frame' | 'video';
  /** In-flight regenerations owned by the clips section, keyed by media id. */
  watches: RegenerateWatchMap;
}

/**
 * Everything the user needs to know about one item's last/current attempt, in
 * the order it matters: an attempt in flight, then the reason the last one
 * failed, then the item's own placeholder state.
 */
const SceneMediaStatus: React.FC<SceneMediaStatusProps> = ({ item, role, watches }) => {
  if (!item) return null;

  const status = regenerateStatusOf(watches, item.id);
  if (status === 'running') {
    return (
      <p
        className="font-mono text-[11px] uppercase tracking-[0.15em] text-amber-300"
        data-testid={`${role}-regenerating`}
      >
        regenerating… this can take a few minutes.
      </p>
    );
  }
  if (status === 'stalled') {
    return (
      <p
        className="font-mono text-[11px] uppercase tracking-[0.15em] text-amber-400"
        data-testid={`${role}-regen-stalled`}
      >
        Still no result — the attempt may have been lost. Try again.
      </p>
    );
  }

  const failure = readRegenerateFailure(item);
  if (failure) {
    return (
      <p
        className="font-mono text-[11px] tracking-[0.05em] text-red-400"
        data-testid={`${role}-regenerate-error`}
      >
        {regenerateFailureText(failure)}
        {role === 'video'
          ? ' — the previous video is still bound.'
          : ' — the previous frame is still bound.'}
      </p>
    );
  }

  if (!item.file_url) return null;
  if (item.file_url.includes('_failed')) {
    return (
      <p
        className="font-mono text-[11px] uppercase tracking-[0.15em] text-red-400"
        data-testid={`${role}-generation-failed`}
      >
        Generation failed — the previous {role} is still bound.
      </p>
    );
  }
  if (item.file_url.includes('_waiting')) {
    return (
      <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
        generating…
      </p>
    );
  }
  return null;
};

export default SceneMediaStatus;
